import { useEffect, useState } from "react";
import { ShieldCheck, Upload } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { PageHeader, SectionCard, SummaryTile } from "@/components/shared/PageChrome";
import { AuditList } from "@/components/supplier/AuditList";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function SupplierCompliance() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(null);
  const [file, setFile] = useState(null);
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const load = () => api.get("/supplier/compliance").then((r) => { setData(r.data); setHistoryKey((k) => k + 1); }).catch(() => setError(true));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!file || !data) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", "supplier");
      fd.append("entity_id", data.supplier_id);
      fd.append("title", `${active.document_type_name} - evidence`);
      fd.append("is_confidential", "true");
      const { data: doc } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/compliance/suppliers/${data.supplier_id}/documents`, {
        document_type_id: active.document_type_id, property_id: active.property_id, document_id: doc.id,
        issued_at: issuedAt || null, expires_at: expiresAt || null,
      });
      toast.success("Compliance evidence uploaded");
      setActive(null);
      setFile(null);
      setIssuedAt("");
      setExpiresAt("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not upload document"); }
    setUploading(false);
  };

  if (error) return <ErrorState testId="supplier-compliance-error" />;
  if (!data) return <LoadingState testId="supplier-compliance-loading" />;

  const counts = data.items.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});
  const mandatoryGaps = data.items.filter((c) => c.is_mandatory && c.status !== "VALID").length;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const daysLeft = (d) => (d ? Math.ceil((new Date(d) - Date.now()) / 86400000) : null);

  return (
    <div data-testid="supplier-compliance-page" className="space-y-5">
      <PageHeader
        title="Compliance & documents"
        subtitle="Mandatory documents must stay valid for your properties to remain available for placement."
        chip={mandatoryGaps > 0 ? <span data-testid="compliance-gap-chip" className="rounded-full border border-jigsaw-danger/30 bg-jigsaw-dangerBg px-2.5 py-0.5 text-[12px] font-medium text-jigsaw-danger">{mandatoryGaps} mandatory {mandatoryGaps === 1 ? "item" : "items"} not valid</span> : <span className="rounded-full border border-jigsaw-success/30 bg-jigsaw-successBg px-2.5 py-0.5 text-[12px] font-medium text-jigsaw-success">All mandatory documents valid</span>}
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SummaryTile testId="compliance-tile-valid" label="Valid" value={counts.VALID || 0} sub={`of ${data.items.length} required documents`} tone="success" />
        <SummaryTile testId="compliance-tile-expiring" label="Expiring soon" value={counts.EXPIRING || 0} sub="Within the renewal window" tone={counts.EXPIRING ? "warning" : "default"} />
        <SummaryTile testId="compliance-tile-expired" label="Expired" value={counts.EXPIRED || 0} sub="Renewal required" tone={counts.EXPIRED ? "danger" : "default"} />
        <SummaryTile testId="compliance-tile-missing" label="Missing" value={counts.MISSING || 0} sub="No evidence on file" tone={counts.MISSING ? "danger" : "default"} />
      </div>

      <SectionCard title="Compliance documents matrix" subtitle="Each row is a required document for your organisation or one of your properties." bodyClassName="px-0 pb-0">
        <ul data-testid="supplier-compliance-mobile-list" className="divide-y divide-jigsaw-border border-t border-jigsaw-border sm:hidden">
          {data.items.map((c, idx) => (
            <li key={`m-${idx}`} className="space-y-2 px-4 py-3">
              <div className="flex items-start justify-between gap-2"><p className="text-[14px] font-medium text-jigsaw-navy">{c.document_type_name}</p><StatusBadge status={c.status} /></div>
              <p className="text-[12px] text-jigsaw-muted">{c.is_mandatory ? "Mandatory" : "Recommended"} · {c.applies_to === "PROPERTY" ? c.property_name : "Supplier-wide"} · Expiry {fmt(c.expires_at)}</p>
              <Button data-testid={`upload-compliance-mobile-${idx}`} size="sm" className="w-full bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark" onClick={() => setActive(c)}><Upload className="mr-1 h-3.5 w-3.5" /> {c.status === "VALID" ? "Replace" : c.status === "MISSING" ? "Upload" : "Upload renewal"}</Button>
            </li>
          ))}
        </ul>
        <div className="hidden sm:block">
        <Table bare data-testid="supplier-compliance-table">
          <TableHeader><TableRow><TableHead>Document type</TableHead><TableHead>Level</TableHead><TableHead>Applies to</TableHead><TableHead>Status</TableHead><TableHead>Expiry</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.items.map((c, idx) => {
              const dl = daysLeft(c.expires_at);
              return (
                <TableRow key={idx} data-testid={`supplier-compliance-row-${idx}`}>
                  <TableCell><span className="flex items-center gap-2 font-medium"><ShieldCheck className="h-4 w-4 shrink-0 text-jigsaw-orange" />{c.document_type_name}</span></TableCell>
                  <TableCell><span className={c.is_mandatory ? "rounded-md bg-jigsaw-navy px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white" : "rounded-md bg-jigsaw-bg px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-jigsaw-muted"}>{c.is_mandatory ? "Mandatory" : "Recommended"}</span></TableCell>
                  <TableCell>{c.applies_to === "PROPERTY" ? c.property_name : "Supplier-wide"}</TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                  <TableCell>
                    <span className="font-mono-ref">{fmt(c.expires_at)}</span>
                    {dl != null && <span className={`block text-[12px] ${dl < 0 ? "text-jigsaw-danger" : dl <= 30 ? "text-jigsaw-warning" : "text-jigsaw-muted"}`}>{dl < 0 ? `${Math.abs(dl)} days overdue` : `${dl} days remaining`}</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`upload-compliance-${idx}`} size="sm" variant={c.status === "VALID" ? "outline" : "default"} className={c.status === "VALID" ? "" : "bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark"} onClick={() => setActive(c)}>
                      <Upload className="mr-1 h-3.5 w-3.5" /> {c.status === "VALID" ? "Replace" : c.status === "MISSING" ? "Upload" : "Upload renewal"}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
        {data.items.length === 0 && <p className="px-5 py-6 text-sm text-jigsaw-muted">No compliance requirements are configured for your organisation yet.</p>}
      </SectionCard>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <AuditList endpoint="/supplier/compliance/history" title="Compliance audit trail" subtitle="Uploads and evidence changes recorded against your documents - previous and current values are shown for each change" testId="compliance-history" emptyText="No compliance activity has been recorded for your documents yet." limit={50} refreshKey={historyKey}
          typeNames={Object.fromEntries(data.items.map((c) => [c.document_type_id, c.document_type_name]))} />
        <SectionCard title="Jigsaw compliance contact" testId="compliance-officer-panel" bodyClassName="px-5 pb-5">
          <p data-testid="compliance-officer-unavailable" className="text-[13px] text-jigsaw-muted">Not configured. Jigsaw has not yet defined a compliance officer role for supplier accounts, so no named contact is shown here. Questions about your documents go through your usual Jigsaw contact.</p>
        </SectionCard>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload evidence - {active?.document_type_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Document file</Label><Input data-testid="compliance-file-input" type="file" onChange={(e) => setFile(e.target.files[0])} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issued on</Label><Input data-testid="compliance-issued-input" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} /></div>
              <div><Label>Expires on</Label><Input data-testid="compliance-expires-input" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="compliance-upload-save-button" onClick={submit} disabled={!file || uploading}>{uploading ? "Uploading..." : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
